import { IntegrationType } from "~/operations";
import { IntegrationAddHeaderData, OtherIntegrations } from "./types";

export const getIntegrationAddHeaderData = (
  type: IntegrationType | OtherIntegrations,
): IntegrationAddHeaderData => {
  switch (type) {
    case IntegrationType.AwsS3:
      return AmazonS3HeaderData;
    case IntegrationType.S3:
      return AmazonS3CompatibleHeaderData;
    case IntegrationType.Azure:
      return AzureHeaderData;
    case IntegrationType.AzureBlob:
      return AzureBlobHeaderData;
    case IntegrationType.Aws:
      return AwsHeaderData;
    case IntegrationType.CrowdstrikeFalcon:
      return CrowdStrikeHeaderData;
    case IntegrationType.Oci:
      return OciHeaderData;
    case IntegrationType.Bigquery:
      return BigQueryHeaderData;
    case IntegrationType.MicrosoftDefender:
      return DefenderHeaderData;
    case IntegrationType.Host:
      return HostHeaderData;
    case IntegrationType.Ms365:
      return MS365HeaderData;
    case IntegrationType.Gcp:
      return GcpHeaderData;
    case IntegrationType.GcsBucket:
      return GcsBucketHeaderData;
    case IntegrationType.Gitlab:
      return GitlabHeaderData;
    case IntegrationType.Github:
      return GithubHeaderData;
    case IntegrationType.TicketSystemJira:
      return JiraHeaderData;
    case IntegrationType.TicketSystemEmail:
      return EmailHeaderData;
    case IntegrationType.TicketSystemWebhook:
      return WebhookHeaderData;
    case IntegrationType.TicketSystemZendesk:
      return ZendeskHeaderData;
    case IntegrationType.TicketSystemGithub:
      return GithubIssuesHeaderData;
    case IntegrationType.TicketSystemGitlab:
      return GitlabIssuesHeaderData;
    case IntegrationType.TicketSystemAzureDevops:
      return AzureDevOpsHeaderData;
    case IntegrationType.TicketSystemServiceNow:
      return ServiceNowHeaderData;
    case IntegrationType.Okta:
      return OktaHeaderData;
    case IntegrationType.HostedSlack:
      return SlackHeaderData;
    case IntegrationType.Snowflake:
      return SnowflakeHeaderData;
    case IntegrationType.SentinelOne:
      return SentinelOneHeaderData;
    case IntegrationType.Postgres:
      return PostgreSQLHeaderData;
    case IntegrationType.GoogleWorkspace:
      return GoogleWorkspaceHeaderData;
    case IntegrationType.K8S:
      return KubernetesHeaderData;
    case IntegrationType.Shodan:
      return ShodanHeaderData;
    case IntegrationType.SecurityPipelineGithub:
      return GithubSecurityPipelineHeaderData;
    case OtherIntegrations.WINDOWS:
      return WindowsHeaderData;
    case OtherIntegrations.LINUX:
      return LinuxHeaderData;
    case OtherIntegrations.CISCO:
      return CiscoHeaderData;
    case OtherIntegrations.PANOS:
      return PanosHeaderData;
    case OtherIntegrations.FORTIOS:
      return FortiosHeaderData;
    case OtherIntegrations.JUNOS:
      return JunosHeaderData;
    case OtherIntegrations.MACOS:
      return MacOsHeaderData;
    case OtherIntegrations.VMWARE:
      return VMwareHeaderData;
    case OtherIntegrations.PACKER:
      return PackerHeaderData;
    case OtherIntegrations.TERRAFORM:
      return TerraformHeaderData;
    case OtherIntegrations.CREDENTIALS:
      return CredentialsHeaderData;
    case OtherIntegrations.WORKSTATION_MONDOO:
      return WorkstationMondooHeaderData;
    case OtherIntegrations.CHATOPS_MSTEAMS:
      return ChatopsMsteamsHeaderData;
    case OtherIntegrations.CHATOPS_SLACK:
      return ChatopsSlackHeaderData;
    case OtherIntegrations.CHATOPS_TELEGRAM:
      return ChatopsTelegramHeaderData;
    case OtherIntegrations.CHATOPS_WEBHOOK:
      return ChatopsWebhookHeaderData;
    default:
      return {
        title: type,
        flags: [],
        tagline: "",
        docsLink: "https://mondoo.com/docs",
      };
  }
};

const AwsHeaderData: IntegrationAddHeaderData = {
  title: "AWS",
  tagline: "Integration for AWS organizations and accounts",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink: "https://mondoo.com/docs/infra/cloud/aws/aws-integration-scan",
};
const OciHeaderData: IntegrationAddHeaderData = {
  title: "Oracle Cloud Infrastructure",
  tagline:
    "INTEGRATION WITH OCI AND ITS EMBEDDED FRAMEWORKS, CLOUDS, AND SERVICES",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink: "https://mondoo.com/docs/infra/cloud/oci",
};
const AzureHeaderData: IntegrationAddHeaderData = {
  title: "Microsoft Azure",
  tagline: "Integration for Microsoft Azure Subscriptions",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink: "https://mondoo.com/docs/infra/cloud/azure/overview",
};
const DefenderHeaderData: IntegrationAddHeaderData = {
  title: "Microsoft Defender for Cloud",
  tagline: "Import security findings from Microsoft Defender for Cloud",
  flags: [
    { id: "continuous_import", title: "Continuous Import" },
    { id: "third_party_data", title: "3rd Party Data" },
  ],
  docsLink: "https://mondoo.com/docs/infra/imports/defender",
};

const SentinelOneHeaderData: IntegrationAddHeaderData = {
  title: "SentinelOne Singularity",
  tagline: "Import security findings from SentinelOne Singularity Platform",
  flags: [
    { id: "continuous_import", title: "Continuous Import" },
    { id: "third_party_data", title: "3rd Party Data" },
  ],
  docsLink: "https://mondoo.com/docs/infra/imports/sentinelone",
};

const CrowdStrikeHeaderData: IntegrationAddHeaderData = {
  title: "CrowdStrike Falcon",
  tagline: "Import security findings from the CrowdStrike Falcon Platform",
  flags: [
    { id: "continuous_import", title: "Continuous Import" },
    { id: "third_party_data", title: "Third-Party Data" },
  ],
  docsLink: "https://mondoo.com/docs/infra/imports/crowdstrike",
};

const AzureBlobHeaderData: IntegrationAddHeaderData = {
  title: "Azure Blob Storage",
  tagline:
    "Continuously export data to Azure Blob Storage in JSONL or CSV format",
  flags: [
    { id: "storage", title: "Storage" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/azure-blob",
};
const BigQueryHeaderData: IntegrationAddHeaderData = {
  title: "BigQuery",
  tagline:
    "Continuously export data to Google Cloud BigQuery for analysis and reporting",
  flags: [
    { id: "data_warehouse", title: "Data Warehouse" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/bigquery-integration",
};

const HostHeaderData: IntegrationAddHeaderData = {
  title: "Domain/IP ADDRESS",
  tagline:
    "Continuously scan endpoints to evaluate domain TLS, SSL, HTTP, and HTTPS security",
  flags: [{ id: "continuous_scanning", title: "Continuous Scanning" }],
  docsLink: "https://mondoo.com/docs/infra/networking/domain-ip",
};

const SnowflakeHeaderData: IntegrationAddHeaderData = {
  title: "Snowflake",
  tagline:
    "Continuously export scan results to Snowflake for storage, analysis, and reporting",
  flags: [
    { id: "data_warehouse", title: "Data Warehouse" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/snowflake",
};
const AmazonS3HeaderData: IntegrationAddHeaderData = {
  title: "Amazon S3",
  tagline: "Continuously export data to Amazon S3 in JSONL or CSV format",
  flags: [
    { id: "storage", title: "Storage" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/s3",
};
const AmazonS3CompatibleHeaderData: IntegrationAddHeaderData = {
  title: "S3 Compatible Service",
  tagline:
    "Continuously export data to Amazon S3-compatible services in JSONL or CSV format",
  flags: [
    { id: "storage", title: "Storage" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/s3-compatible",
};
const PostgreSQLHeaderData: IntegrationAddHeaderData = {
  title: "PostgreSQL",
  tagline: "Continuously export data to PostgreSQL databases",
  flags: [
    { id: "database", title: "Database" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/postgresql",
};

const CloudSQLHeaderData: IntegrationAddHeaderData = {
  title: "GCP Cloud SQL for PostgreSQL",
  tagline: "Continuously export data to GCP Cloud SQL for PostgreSQL",
  flags: [
    { id: "database", title: "Database" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/postgresql",
};

const GcsBucketHeaderData: IntegrationAddHeaderData = {
  title: "GCP Cloud storage bucket",
  tagline:
    "Continuously export data to GCP Cloud Storage in JSONL or CSV format",
  flags: [
    { id: "storage", title: "Storage" },
    { id: "exports", title: "Exports" },
  ],
  docsLink: "https://mondoo.com/docs/reporting/export/gcs-bucket",
};

const GcpHeaderData: IntegrationAddHeaderData = {
  title: "Google Cloud Platform",
  tagline: "Integration for GCP Organizations and Projects",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink: "https://mondoo.com/docs/infra/cloud/gcp/gcp-integration-scan",
};

const KubernetesHeaderData: IntegrationAddHeaderData = {
  title: "Kubernetes Operator Configuration",
  tagline: "Continuous Kubernetes cluster, workload, and container scanning",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink:
    "https://mondoo.com/docs/infra/cloud/kubernetes/scan-kubernetes-with-operator",
};

const ShodanHeaderData: IntegrationAddHeaderData = {
  title: "Shodan",
  tagline: "Continuously assess external risk for domains and IP addresses",
  flags: [{ id: "networking", title: "Networking" }],
  docsLink: "https://mondoo.com/docs/infra/networking/shodan",
};

const MS365HeaderData: IntegrationAddHeaderData = {
  title: "Microsoft 365",
  tagline: "Continuously scan Microsoft 365 for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/m365/m365-auto",
};

const GithubHeaderData: IntegrationAddHeaderData = {
  title: "GitHub",
  tagline:
    "Continuously scan your GitHub organizations and repositories for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/github",
};

const JiraHeaderData: IntegrationAddHeaderData = {
  title: "Jira",
  tagline:
    "Integrate Mondoo with Jira to create Jira issues from the Mondoo Console",
  flags: [{ id: "ticketing", title: "Ticket Systems" }],
  docsLink: "https://mondoo.com/docs/ticketing/setup-jira",
};

const EmailHeaderData: IntegrationAddHeaderData = {
  title: "Ticketing via email",
  tagline: "Send email to a listener or service when a new ticket is created",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/setup-email",
};

const WebhookHeaderData: IntegrationAddHeaderData = {
  title: "Webhook ticketing",
  tagline: "Create and manage tickets through JSON webhooks",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/overview",
};

const ZendeskHeaderData: IntegrationAddHeaderData = {
  title: "Zendesk",
  tagline:
    "INTEGRATE MONDOO WITH ZENDESK TO CREATE ZENDESK TICKETS FROM THE MONDOO CONSOLE",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/setup-zendesk",
};

const GithubIssuesHeaderData: IntegrationAddHeaderData = {
  title: "GitHub Issues",
  tagline:
    "INTEGRATE MONDOO WITH GITHUB TO CREATE GITHUB ISSUES FROM THE MONDOO CONSOLE",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/setup-github",
};

const GitlabIssuesHeaderData: IntegrationAddHeaderData = {
  title: "GitLab Issues",
  tagline:
    "INTEGRATE MONDOO WITH GITLAB TO CREATE GITLAB ISSUES FROM THE MONDOO CONSOLE",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/setup-gitlab",
};

const AzureDevOpsHeaderData: IntegrationAddHeaderData = {
  title: "Azure DevOps",
  tagline:
    "INTEGRATE MONDOO WITH AZURE DEVOPS TO CREATE ISSUES FROM THE MONDOO CONSOLE",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/setup-azuredevops",
};

const ServiceNowHeaderData: IntegrationAddHeaderData = {
  title: "ServiceNow",
  tagline:
    "Integrate Mondoo with ServiceNow to create ServiceNow incidents from the Mondoo Console",
  flags: [
    { id: "tickets", title: "Ticketing" },
    { id: "workflows", title: "Workflows" },
  ],
  docsLink: "https://mondoo.com/docs/ticketing/overview",
};

const OktaHeaderData: IntegrationAddHeaderData = {
  title: "Okta",
  tagline: "Continuously scan your Okta domain for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/okta",
};

const GitlabHeaderData: IntegrationAddHeaderData = {
  title: "GitLab",
  tagline:
    "Continuously scan your GitLab groups for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/gitlab",
};

const SlackHeaderData: IntegrationAddHeaderData = {
  title: "Slack",
  tagline: "Continuously scan your Slack Teams for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/slack",
};

const GoogleWorkspaceHeaderData: IntegrationAddHeaderData = {
  title: "Google Workspace",
  tagline:
    "Continuously scan your Google Workspace environment for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "saas", title: "SaaS" },
  ],
  docsLink: "https://mondoo.com/docs/infra/saas/google_workspace",
};

const WindowsHeaderData: IntegrationAddHeaderData = {
  title: "Windows",
  tagline: "Continuously scan Windows hosts for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "operating_system", title: "Operating System" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/cnspec-adv-install/windows",
};

const LinuxHeaderData: IntegrationAddHeaderData = {
  title: "Linux",
  tagline: "Continuously scan Linux hosts for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "operating_system", title: "Operating System" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/cnspec-adv-install/linux",
};

const CiscoHeaderData: IntegrationAddHeaderData = {
  title: "Cisco",
  tagline:
    "Scan Cisco IOS XR, IOS XE, and NX-OS devices for security misconfigurations and vulnerabilities",
  flags: [{ id: "network_devices", title: "Network Devices" }],
  docsLink: "https://mondoo.com/docs/cnspec/network/cisco",
};

const PanosHeaderData: IntegrationAddHeaderData = {
  title: "Palo Alto Networks PAN-OS",
  tagline:
    "Scan Palo Alto Networks PAN-OS devices for vulnerabilities and EOL status",
  flags: [{ id: "network_devices", title: "Network Devices" }],
  docsLink: "https://mondoo.com/docs/cnspec/network/panos",
};

const FortiosHeaderData: IntegrationAddHeaderData = {
  title: "Fortinet FortiOS",
  tagline: "Scan Fortinet FortiOS devices for vulnerabilities and EOL status",
  flags: [{ id: "network_devices", title: "Network Devices" }],
  docsLink: "https://mondoo.com/docs/cnspec/network/fortios",
};

const JunosHeaderData: IntegrationAddHeaderData = {
  title: "Juniper Networks Junos",
  tagline: "Scan Juniper Networks Junos devices for vulnerabilities",
  flags: [{ id: "network_devices", title: "Network Devices" }],
  docsLink: "https://mondoo.com/docs/cnspec/network/junos",
};

const MacOsHeaderData: IntegrationAddHeaderData = {
  title: "macOS",
  tagline: "Continuously scan macOS hosts for security misconfigurations",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "operating_system", title: "Operating System" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/cnspec-adv-install/macos",
};

const VMwareHeaderData: IntegrationAddHeaderData = {
  title: "VMware",
  tagline:
    "Deploy the Mondoo VMware Appliance to scan VMware vCenter Server and ESXi",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "cloud", title: "Cloud" },
  ],
  docsLink: "https://mondoo.com/docs/infra/cloud/vmware",
};

const PackerHeaderData: IntegrationAddHeaderData = {
  title: "HashiCorp Packer",
  tagline: "The HashiCorp Packer plugin by Mondoo",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "iac", title: "Infrastructure as Code" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/supplychain/packer",
};

const TerraformHeaderData: IntegrationAddHeaderData = {
  title: "HashiCorp Terraform",
  tagline:
    "Use Mondoo to run a static analysis of HashiCorp Terraform (HCL) code",
  flags: [
    { id: "continuous_scanning", title: "Continuous Scanning" },
    { id: "iac", title: "Infrastructure as Code" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/supplychain/terraform",
};

const CredentialsHeaderData: IntegrationAddHeaderData = {
  title: "Generate Long-Lived Credentials",
  tagline: "Generate and download Mondoo Platform credentials.",
  flags: [],
  docsLink:
    "https://mondoo.com/docs/cnspec/cnspec-adv-install/registration-keys",
};

const WorkstationMondooHeaderData: IntegrationAddHeaderData = {
  title: "Engineering Workstation",
  tagline: "LOCAL CNSPEC AND CNQUERY INSTALLATION FOR ENGINEERS",
  flags: [
    { id: "agentless_scanning", title: "Agentless Scanning" },
    { id: "local_scanning", title: "Local Scanning" },
    { id: "remote_scanning", title: "Remote Scanning" },
  ],
  docsLink: "https://mondoo.com/docs/cnspec/cnspec-adv-install/overview",
};

// CHAT OPS DATA

const ChatopsMsteamsHeaderData: IntegrationAddHeaderData = {
  title: "Microsoft Teams",
  tagline: "Microsoft Teams is the hub for team collaboration in Microsoft 365",
  flags: [{ id: "chat_ops", title: "Chat Ops" }],
  docsLink: "https://mondoo.com/docs/maintain/alerting/msteams",
};

const ChatopsSlackHeaderData: IntegrationAddHeaderData = {
  title: "Slack Notifications",
  tagline: "Slack is a cloud-based proprietary instant messaging platform",
  flags: [{ id: "chat_ops", title: "Chat Ops" }],
  docsLink: "https://mondoo.com/docs/maintain/alerting/slack",
};
const ChatopsTelegramHeaderData: IntegrationAddHeaderData = {
  title: "Telegram",
  tagline: "Telegram is a cloud-based instant messaging service",
  flags: [{ id: "chat_ops", title: "Chat Ops" }],
  docsLink: "https://mondoo.com/docs/maintain/alerting/telegram",
};
const ChatopsWebhookHeaderData: IntegrationAddHeaderData = {
  title: "Outbound Webhook",
  tagline: "A webhook delivers event data to a HTTP endpoint",
  flags: [{ id: "chat_ops", title: "Chat Ops" }],
  docsLink: "https://mondoo.com/docs/maintain/alerting/webhook",
};

const GithubSecurityPipelineHeaderData: IntegrationAddHeaderData = {
  title: "GitHub Remediation Workflow",
  tagline: "Don’t just find problems, fix them. Powered by Ansible.",
  flags: [],
  docsLink: "https://mondoo.com/docs/infra/saas/github",
};
